import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { EntityResolved } from '@toggle/toggle';
import React, { RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RowSelectionCheckbox } from '~/components/row-selection-checkbox/RowSelectionCheckbox';
import { UseRowsSelectionReturnType } from '~/hooks/use-rows-selection/useRowsSelection';
import { useOnboardingStore } from '~/views/onboarding/hooks/useOnboardingStore';

import { DraggableWatchlistRow } from '../draggable-watchlist-row/DraggableWatchlistRow';
import { useTableShadow } from './hooks/useTableShadow';
import { EntityCellBaseHandlers } from './table-cells/entity-cell/EntityCell';
import * as S from './TableWatchlistManager.styles';
import { getColumns, INPUT_HEADER_COLUMN_ID } from './utils';

export interface TableWatchlistManagerProps
  extends Omit<UseRowsSelectionReturnType, 'setRowSelection'> {
  data: EntityResolved[];
  countEntitiesAdded: number;
  shadowContainerRef: RefObject<HTMLDivElement | null>;
  unMappedRowRef?: RefObject<HTMLTableRowElement | null>;
  isEmptyListError?: boolean;
  handlers: EntityCellBaseHandlers;
  showIndustryColumn?: boolean;
}

export const TableWatchlistManager = ({
  data,
  countEntitiesAdded,
  toggleAllRowsSelection,
  rowSelection,
  updateRowSelection,
  shadowContainerRef,
  unMappedRowRef,
  isEmptyListError,
  handlers,
  showIndustryColumn,
}: TableWatchlistManagerProps) => {
  const { t } = useTranslation(['watchlistManager', 'analyze']);
  const { unMappedEntitiesList, entitiesList } = useOnboardingStore(state => ({
    unMappedEntitiesList: state.unMappedEntitiesList,
    entitiesList: state.entitiesList,
  }));

  const includeInput =
    !!unMappedEntitiesList.length || entitiesList.some(e => e.input);
  const optionalColumns = {
    inputColumn: includeInput,
    industryColumn: !!showIndustryColumn,
  };
  const columns = useMemo(
    () =>
      getColumns({
        t,
        toggleAllRowsSelection,
        optionalColumns,
        handlers,
      }),
    [includeInput, showIndustryColumn]
  );

  useTableShadow({ data, shadowContainerRef });
  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: data => {
      const resolvedData = data as EntityResolved;
      return resolvedData.tag;
    },
  });
  const tableRows = table.getRowModel().rows;

  const unMappedTable = useReactTable({
    data: unMappedEntitiesList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: data => data.input,
  });
  const unMappedTableRows = unMappedTable.getRowModel().rows;

  return (
    <S.ShadowContainer ref={shadowContainerRef}>
      <S.StyledTanStackTableRoot>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <S.StyledTableHeader
                  alignment="left"
                  key={header.id}
                  $error={isEmptyListError}
                  $isSmall={header.column.id === INPUT_HEADER_COLUMN_ID}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </S.StyledTableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {!!data.length && (
            <>
              <S.StyledFullRow>
                <S.StyledHeaderCell
                  alignment="left"
                  colspan={table.getHeaderGroups()[0].headers.length}
                  id="confirm-assets-table-header"
                  data-testid="confirm-assets-table-header"
                >
                  <S.HeaderCellLabel>
                    {t('watchlistManager:confirmMapping')}
                  </S.HeaderCellLabel>
                </S.StyledHeaderCell>
              </S.StyledFullRow>
              {tableRows.map((row, index) => {
                const isNew = index < countEntitiesAdded;
                return (
                  <DraggableWatchlistRow
                    data-testid="created-watchlist-row"
                    key={row.id}
                    row={row}
                    afterNode={
                      <RowSelectionCheckbox
                        name={`watchlist-${row.id}`}
                        checked={row.getIsSelected()}
                        onChange={() =>
                          updateRowSelection(row.id, !row.getIsSelected())
                        }
                        variant={isEmptyListError ? 'critical' : 'primary'}
                      />
                    }
                  >
                    {row.getVisibleCells().map(cell => {
                      return (
                        <S.StyledTableCell
                          key={cell.id}
                          $isNew={isNew}
                          isNumber={false}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </S.StyledTableCell>
                      );
                    })}
                  </DraggableWatchlistRow>
                );
              })}
            </>
          )}
          {!!unMappedEntitiesList.length && (
            <S.StyledFullRow ref={unMappedRowRef}>
              <S.StyledHeaderCell
                alignment="left"
                colspan={table.getHeaderGroups()[0].headers.length}
                id="unmapped-table-header"
                data-testid="unmapped-table-header"
              >
                <S.HeaderCellLabel>
                  {t('watchlistManager:unmappedAssets')}
                </S.HeaderCellLabel>
              </S.StyledHeaderCell>
            </S.StyledFullRow>
          )}
          {unMappedTableRows.map(row => (
            <DraggableWatchlistRow
              key={row.id}
              row={row}
              afterNode={
                <S.StyledIcon
                  iconName="QuestionMarkCircle"
                  fillColor="var(--icon-critical)"
                  size={20}
                />
              }
            >
              {row.getVisibleCells().map(cell => {
                return (
                  <S.StyledTableCell key={cell.id} isNumber={false}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </S.StyledTableCell>
                );
              })}
            </DraggableWatchlistRow>
          ))}
        </tbody>
      </S.StyledTanStackTableRoot>
    </S.ShadowContainer>
  );
};

TableWatchlistManager.Styled = S;
